/**
 * System status.
 *
 * This checks the thing it is describing rather than reading a number someone
 * typed in: it calls the API's health endpoint from the reader's own browser,
 * so a green light here means the reader can reach us, which is the only claim
 * a status page is really able to make.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PublicFooter, PublicHeader } from './PublicPage.js';

type State = 'wait' | 'up' | 'down';

interface Check {
  key: string;
  label: string;
  detail: string;
  state: State;
  ms: number | null;
}

const INITIAL: Check[] = [
  { key: 'api', label: 'API', detail: 'Reservations, records and billing', state: 'wait', ms: null },
  { key: 'app', label: 'Application', detail: 'The client this page is served from', state: 'wait', ms: null },
];

export function Status(): JSX.Element {
  const [checks, setChecks] = useState<Check[]>(INITIAL);
  const [at, setAt] = useState<Date | null>(null);
  const [busy, setBusy] = useState(false);

  const probe = useCallback(async () => {
    setBusy(true);
    const started = performance.now();
    let api: State = 'down';
    try {
      const res = await fetch('/api/health', { cache: 'no-store' });
      api = res.ok ? 'up' : 'down';
    } catch {
      api = 'down';
    }
    const ms = Math.round(performance.now() - started);

    setChecks([
      { ...INITIAL[0], state: api, ms },
      // The page is running, so its own assets are being served by definition.
      { ...INITIAL[1], state: 'up', ms: null },
    ]);
    setAt(new Date());
    setBusy(false);
  }, []);

  useEffect(() => {
    void probe();
    const timer = setInterval(() => void probe(), 30_000);
    return () => clearInterval(timer);
  }, [probe]);

  const allUp = checks.every((c) => c.state === 'up');
  const anyDown = checks.some((c) => c.state === 'down');

  return (
    <div className="lp">
      <PublicHeader />
      <div className="lp__doc">
        <h1 className="lp__doct">
          {anyDown ? 'Something is down' : allUp ? 'All systems operational' : 'Checking…'}
        </h1>
        <p className="lp__docl">
          This page checks FlightSlots from your browser, right now. A green light means your
          connection reached us and we answered.
        </p>

        <div className="card lp__list" style={{ marginTop: 26 }}>
          {checks.map((check) => (
            <div className="lp__status" key={check.key}>
              <span className={`lp__statusd lp__statusd--${check.state}`} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14.5, fontWeight: 600 }}>{check.label}</span>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--label-3)' }}>{check.detail}</span>
              </span>
              <span className="lp__reg">
                {check.state === 'wait' ? 'checking' : check.state === 'up' ? 'operational' : 'unreachable'}
                {check.ms != null && check.state === 'up' ? ` · ${check.ms} ms` : ''}
              </span>
            </div>
          ))}
        </div>

        <div className="lp__actions" style={{ justifyContent: 'flex-start', marginTop: 20 }}>
          <button className="btn" onClick={() => void probe()} disabled={busy}>
            {busy ? 'Checking…' : 'Check again'}
          </button>
          <span className="lp__unit" style={{ alignSelf: 'center' }}>
            {at ? `Last checked ${at.toLocaleTimeString()}. Rechecks every 30 seconds.` : ''}
          </span>
        </div>

        <p className="note" style={{ marginTop: 24 }}>
          FlightSlots runs as one machine with one disk, on purpose — the schedule is a SQLite
          database and a second instance would quietly get its own copy of the school. That means a
          deploy is a short restart rather than a rolling one, and the{' '}
          <Link to="/security">security page</Link> describes how the data survives it.
        </p>
        <p className="note">
          If this page is green and something is still wrong for you,{' '}
          <Link to="/contact?about=support">tell us what you are seeing</Link>.
        </p>
      </div>
      <PublicFooter />
    </div>
  );
}
